<template>
  <div class="min-h-screen bg-gray-50">
   
    <!-- Profile content when authenticated -->
    <div >
      <!-- Header -->
      <header class="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div class="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" class="flex items-center gap-2">
            <img src="/images/logo.png" alt="Logo" class="h-8 w-auto" />
          </a>
          <h1 class="text-xl font-bold text-gray-900">Mon Profil</h1>
          <button
            @click="logout"
            class="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
          >
            Déconnexion
          </button>
        </div>
      </header>

      <!-- Profile Content -->
      <main class="max-w-4xl mx-auto px-4 py-8">
        <!-- User Info Card -->
        <div class="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div class="flex items-center gap-4">
            <div class="w-16 h-16 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center">
              <span class="text-white text-xl font-bold">{{ userInitials }}</span>
            </div>
            <div>
              <h2 class="text-xl font-bold text-gray-900">{{ userName }}</h2>
              <p class="text-gray-500">{{ userEmail }}</p>
            </div>
          </div>
        </div>

        <!-- Stats -->
        <div class="grid grid-cols-3 gap-4 mb-6">
          <div class="bg-white rounded-2xl shadow-sm p-4 text-center">
            <div class="text-2xl font-bold text-blue-600">{{ favoriteCount }}</div>
            <div class="text-sm text-gray-500">Favoris</div>
          </div>
          <div class="bg-white rounded-2xl shadow-sm p-4 text-center">
            <div class="text-2xl font-bold text-blue-600">{{ bookingsCount }}</div>
            <div class="text-sm text-gray-500">Réservations</div>
          </div>
          <div class="bg-white rounded-2xl shadow-sm p-4 text-center">
            <div class="text-2xl font-bold text-blue-600">{{ eventsAttended }}</div>
            <div class="text-sm text-gray-500">Événements</div>
          </div>
        </div>

        <!-- Menu Options -->
        <div class="bg-white rounded-2xl shadow-sm overflow-hidden mb-6">
          <router-link
            to="/bookings"
            class="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-100"
          >
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                </svg>
              </div>
              <span class="font-medium text-gray-900">Mes billets</span>
            </div>
            <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </router-link>

          <router-link
            to="/favorites"
            class="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-100"
          >
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                </svg>
              </div>
              <span class="font-medium text-gray-900">Mes favoris</span>
            </div>
            <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </router-link>

          <button class="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 w-full">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426-1.756-2.924-1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
              </div>
              <span class="font-medium text-gray-900">Paramètres</span>
            </div>
            <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>

          <button class="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors w-full">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3c0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <span class="font-medium text-gray-900">Aide</span>
            </div>
            <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();

const isAuthenticated = ref(false);
const userEmail = ref('');
const userName = ref('');
const favoriteCount = ref(0);
const bookingsCount = ref(0);
const eventsAttended = ref(0);

const userInitials = computed(() => {
  if (!userName.value) return '?';
  return userName.value.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
});

const checkAuth = () => {
  const userStore = window.$stores?.user;
  if (userStore?.state?.isLoggedIn) {
    isAuthenticated.value = true;
    userName.value = userStore.state.userName || 'Utilisateur';
    userEmail.value = userStore.state.email || '';
  } else {
    // Check localStorage as fallback
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    if (isLoggedIn) {
      isAuthenticated.value = true;
      userName.value = localStorage.getItem('userName') || 'Utilisateur';
      userEmail.value = localStorage.getItem('userEmail') || '';
    }
  }
};

const loadFavorites = () => {
  const saved = localStorage.getItem('labilleterie-favorites');
  if (saved) {
    favoriteCount.value = JSON.parse(saved).length;
  }
};

const logout = () => {
  localStorage.removeItem('isLoggedIn');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userName');

  // Also clear from store if available
  const userStore = window.$stores?.user;
  if (userStore) {
    window.$bus.emit('EVENTS.USER.LOGOUT');
  }

  router.push('/');
};

onMounted(() => {
  checkAuth();
  loadFavorites();
});
</script>

<style scoped>
</style>
